import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { WebpageTabOptions } from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";

import { useCMSTabContext } from "../cms-tab-context";

const cmsWebpageTabSettingsFormSchema = z.object({
  webpage_url: z.url({
    hostname: z.regexes.domain,
  }),
  sync_with_tamper_status: z.boolean(),
});

type Props = {
  options: WebpageTabOptions;
};

const WebpageTabSettings: FC<Props> = ({ options }) => {
  const { tabIndex } = useCMSTabContext();
  const onUpdate = useCMSStore.use.onUpdateWebpageTabOptions();

  const form = useForm({
    resolver: zodResolver(cmsWebpageTabSettingsFormSchema),
    defaultValues: {
      webpage_url: get(options, "webpage_url", ""),
      sync_with_tamper_status: get(options, "sync_with_tamper_status", false),
    },
    mode: "onChange",
  });

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        onUpdate(tabIndex, { ...options, ...values });
      },
    });

    return unsubscribe;
  }, [form, onUpdate, options, tabIndex]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium leading-none">
          Settings
        </CardTitle>
        <CardDescription>
          Embed any webpage built from visual editors like Webflow, Framer, and
          Builder by entering the page path or URL below.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <CardContent className="text-sm space-y-4">
          <FormField
            control={form.control}
            name="webpage_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>URL</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="https://www.example.com" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sync_with_tamper_status"
            render={({ field }) => (
              <FormItem className="flex justify-between items-center">
                <div className="space-y-1.5">
                  <FormLabel>Sync with Tamper Tag</FormLabel>
                  <FormDescription>
                    Lock the contents of this tab until a consumer breaks the
                    seal of the tamper tag.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </CardContent>
      </Form>
    </Card>
  );
};

export default WebpageTabSettings;
